import React from 'react';
import styles from './Listing.module.css';
import { useTheme } from '../contexts/ThemeContext';
import Card from './Card';
import EmptyState from './EmptyState';

/**
 * Listing Component - A standard component for displaying collections of items
 * 
 * @param {Object} props
 * @param {Array} props.items - Data to display
 * @param {Function} props.renderItem - Function to render each item
 * @param {string} props.variant - 'list' or 'grid' (default: 'list')
 * @param {string} props.title - Optional title for the listing
 * @param {React.ReactNode} props.actions - Optional actions to display in header
 * @param {React.ReactNode} props.emptyState - Optional custom empty state
 * @param {string} props.emptyTitle - Title for default empty state
 * @param {string} props.emptyMessage - Message for default empty state
 * @param {React.ReactNode} props.emptyIcon - Icon for default empty state
 * @param {string} props.className - Additional class names
 */
const Listing = ({
    items = [],
    renderItem,
    variant = 'list',
    title,
    actions,
    emptyState,
    emptyTitle = 'No items found',
    emptyMessage = 'There are no items to display at the moment.',
    emptyIcon,
    className = '',
    ...props
}) => {
    const { theme } = useTheme();

    if (!items || items.length === 0) {
        if (emptyState) return emptyState;
        return (
            <div className={`${styles.listingWrapper} ${className}`}>
                {title && (
                    <div className={styles.listingHeader}>
                        <h2 className={styles.listingTitle}>{title}</h2>
                        {actions && <div className={styles.listingActions}>{actions}</div>}
                    </div>
                )}
                <Card variant="transparent">
                    <EmptyState
                        title={emptyTitle}
                        description={emptyMessage}
                        icon={emptyIcon}
                    />
                </Card>
            </div>
        );
    }

    return (
        <div className={`${styles.listingWrapper} ${className}`} {...props}>
            {(title || actions) && (
                <div className={styles.listingHeader}>
                    {title && <h2 className={styles.listingTitle}>{title}</h2>}
                    {actions && <div className={styles.listingActions}>{actions}</div>}
                </div>
            )}
            <div className={styles[variant]}>
                {items.map((item, index) => (
                    <div key={item.id || index} className={styles.listItem}>
                        {renderItem(item, index)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Listing;
